import React from 'react';
import { X } from 'lucide-react';

interface TermsAndConditionsProps {
  isOpen: boolean;
  onClose: () => void;
}

const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] w-full overflow-hidden">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Terms & Conditions</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-80px)]">
          <div className="prose prose-sm max-w-none">
            <h1 className="text-xl font-bold mb-4">EJL Education – Terms & Conditions (Formal Legal Version)</h1>
            
            <section className="mb-6">
              <h2 className="text-lg font-semibold mb-2">Parties</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms & Conditions ("Agreement") are made between the individual trader Ethan Langley, 
                trading as EJL Education ("Tutor"), and the individual or parent/guardian making a booking for 
                tutoring services ("Client"). By engaging in tutoring services, the Client commits himself/herself 
                to this Agreement.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-lg font-semibold mb-2">Services</h2>
              <p className="text-gray-700 leading-relaxed">
                The Tutor undertakes private tutoring in specified subjects and levels (11+, GCSE, A-Level). 
                Sessions shall either be conducted face-to-face at a mutually agreed location or online using
                a video conferencing platform. The Tutor will provide lesson materials, quizzes, and feedback as and when necessary.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-lg font-semibold mb-2">Bookings & Scheduling</h2>
              <p className="text-gray-700 leading-relaxed">
                The Client shall pre-book sessions with the Tutor. Cancellation or rescheduling by the Client shall 
                be done at least 24 hours in advance. Failure of such notice shall be charged in full for the session. 
                In the event of cancellation by the Tutor, the session shall either be refunded or rescheduled.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-lg font-semibold mb-2">Payments</h2>
              <p className="text-gray-700 leading-relaxed">The Client shall pay for services as follows:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-700">
                <li>
                  <strong>Individual lessons:</strong> Prepayment is preferable, but can be made at the end of the 
                  lesson.
                </li>
                <li>
                  <strong>Packages:</strong> All package fees (10/20/30 hours) must be prepaid prior to the 
                  commencement of the first lesson.
                </li>
                <li>
                  <strong>Overdue payments:</strong> In case of non-payment within seven (7) calendar days from the 
                  lesson date, there will be a ten percent (10%) interest surcharge on the outstanding balance. 
                  The Tutor has the right to withhold subsequent services until all due payments are paid.
                </li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-lg font-semibold mb-2">Refunds & Credits</h2>
              <p className="text-gray-700 leading-relaxed">
                All package payments and lesson fees are non-refundable. On the Tutor's discretion, lessons may be 
                converted into equivalent credits to use in the future. Refunds are provided under exceptional 
                circumstances at the discretion of the Tutor.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-lg font-semibold mb-2">Lesson Delivery & Technical Issues</h2>
              <p className="text-gray-700 leading-relaxed">
                The Tutor will conduct online classes through Microsoft Teams. The Client is solely liable for 
                ensured internet connectivity, functional audio/visual equipment, and adequate environment. 
                The Tutor will not be liable for disruptions or failures due to Client-side technical issues.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-lg font-semibold mb-2">Progress Disclaimer</h2>
              <p className="text-gray-700 leading-relaxed">
                The Tutor provides personalized academic support, but explicitly disclaims any promise of particular 
                outcomes, results, or grades. Student progress depends on a multitude of factors including, but not 
                limited to: student effort, homework completion, attendance, punctuality, home life, and learning 
                environment. The Client acknowledges that final responsibility for academic work rests with the 
                student.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-lg font-semibold mb-2">Intellectual Property</h2>
              <p className="text-gray-700 leading-relaxed">
                All materials, worksheets, quizzes, and resources made available by the Tutor are and shall remain 
                the property of EJL Education. These materials are provided for the exclusive personal educational 
                use of the Client and should not be copied, forwarded, sold, or otherwise utilized without prior 
                written authorization from the Tutor.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-lg font-semibold mb-2">Code of Conduct</h2>
              <p className="text-gray-700 leading-relaxed">
                The Client promises to make the student respect people and be punctual in class. Inappropriate, 
                disrupting, or unsafe behavior may cause termination of services at once with no refund. Where the 
                student is below 18 years, there should be an adult responsible at home for the entire duration of 
                lessons.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-lg font-semibold mb-2">Termination</h2>
              <p className="text-gray-700 leading-relaxed">
                Either side is allowed to terminate this Agreement on two (2) weeks' written notice. The Client 
                shall pay all fees up to the notice date. The Tutor is allowed to terminate at short notice in 
                cases of misconduct or persistent late payment.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-lg font-semibold mb-2">Governing Law</h2>
              <p className="text-gray-700 leading-relaxed">
                This Agreement and all matters in relation thereto shall be governed by and construed according 
                to the laws of England and Wales. The parties hereto submit to the exclusive jurisdiction of the 
                courts of England and Wales with regard to any dispute under this Agreement.
              </p>
            </section>

            <div className="mt-8 pt-4 border-t text-center text-sm text-gray-600">
              <p>Last updated: 2 September 2024</p>
              <p className="mt-2">© {new Date().getFullYear()} EJL Education. All rights reserved.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;